import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {Add as AddIcon, CalendarMonth as CalendarIcon, Delete as DeleteIcon, Edit as EditIcon, Event as EventIcon, Group as GroupIcon, LocationOn as LocationIcon, TableRows as TableIcon, ViewModule as CardIcon,} from '@mui/icons-material';
import eventService from '../../services/eventService';

const ManageEvents = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [viewMode, setViewMode] = useState('cards'); // 'table' oder 'cards' - Standard: cards

    // Dialog States
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [deletingEvent, setDeletingEvent] = useState(null);
    const [useDuration, setUseDuration] = useState(true); // true = Dauer-Modus, false = manuelles Enddatum

    // Form Data
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        eventDate: '',
        eventEndDate: '',
        duration: '120', // Dauer in Minuten (Standard: 2 Stunden)
        location: '',
        type: 'UEBUNGSDIENST',
    });

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            setLoading(true);
            const data = await eventService.getAllEvents();
            setEvents(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Fehler beim Laden der Events:', err);
            setError('Fehler beim Laden der Events: ' + (err.response?.data?.message || err.message));
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };


    const handleOpenDialog = (event = null) => {
        if (event) {
            setEditingEvent(event);
            // Prüfe, ob ein Enddatum vorhanden ist
            const hasEndDate = event.eventEndDate && event.eventEndDate !== event.eventDate;
            setUseDuration(!hasEndDate);

            setFormData({
                title: event.title,
                description: event.description || '',
                eventDate: event.eventDate ? formatDateTimeLocal(event.eventDate) : '',
                eventEndDate: event.eventEndDate ? formatDateTimeLocal(event.eventEndDate) : '',
                duration: '120',
                location: event.location || '',
                type: event.type || 'UEBUNGSDIENST',
            });
        } else {
            setEditingEvent(null);
            setUseDuration(true);
            setFormData({
                title: '',
                description: '',
                eventDate: '',
                eventEndDate: '',
                duration: '120',
                location: '',
                type: 'UEBUNGSDIENST',
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingEvent(null);
        setUseDuration(true);
        setFormData({
            title: '',
            description: '',
            eventDate: '',
            eventEndDate: '',
            duration: '120',
            location: '',
            type: 'UEBUNGSDIENST',
        });
    };

    const formatDateTimeLocal = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const offset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - (offset * 60 * 1000));
        return localDate.toISOString().slice(0, 16);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Submitting form data:', formData);
        setError('');
        setSuccess('');

        try {
            let endDate = null;

            // Berechne Enddatum basierend auf Modus
            if (useDuration && formData.eventDate && formData.duration) {
                const startDate = new Date(formData.eventDate);
                const durationMinutes = parseInt(formData.duration);
                endDate = new Date(startDate.getTime() + durationMinutes * 60000);
            } else if (!useDuration && formData.eventEndDate) {
                endDate = new Date(formData.eventEndDate);
            }

            const submitData = {
                title: formData.title,
                description: formData.description,
                location: formData.location,
                type: formData.type,
                eventDate: formData.eventDate ? new Date(formData.eventDate).toISOString() : null,
                eventEndDate: endDate ? endDate.toISOString() : null,
            };

            if (editingEvent) {
                await eventService.updateEvent(editingEvent.id, submitData);
                setSuccess('Event erfolgreich aktualisiert!');
            } else {
                await eventService.createEvent(submitData);
                setSuccess('Event erfolgreich erstellt! Teilnehmer wurden automatisch hinzugefügt.');
            }
            handleCloseDialog();
            loadEvents();
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Speichern des Events');
        }
    };

    const handleOpenDeleteDialog = (event) => {
        setDeletingEvent(event);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setDeletingEvent(null);
    };

    const handleDelete = async () => {
        try {
            await eventService.deleteEvent(deletingEvent.id);
            setSuccess('Event erfolgreich gelöscht!');
            handleCloseDeleteDialog();
            loadEvents();
        } catch (err) {
            setError('Fehler beim Löschen des Events: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleViewEvent = (eventId) => {
        navigate(`/events/${eventId}`);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getUpcomingEvents = () => {
        const now = new Date();
        return events
            .filter(event => new Date(event.eventDate) >= now)
            .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate)); // Aufsteigend (nächster zuerst)
    };

    const getPastEvents = () => {
        const now = new Date();
        return events
            .filter(event => new Date(event.eventDate) < now)
            .sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate)); // Absteigend (neueste zuerst)
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{mt: 4}}>
                <Typography>Lade Events...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
            <Box sx={{mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <EventIcon sx={{fontSize: 40, color: 'primary.main'}}/>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Übungsdienste
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Verwalten Sie Ihre Übungsdienste und Anwesenheiten
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newMode) => newMode && setViewMode(newMode)}
                        aria-label="Ansicht wechseln"
                        size="small"
                    >
                        <ToggleButton value="table" aria-label="Tabellenansicht">
                            <TableIcon sx={{mr: {xs: 0, sm: 1}}}/>
                            <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>
                                Tabelle
                            </Box>
                        </ToggleButton>
                        <ToggleButton value="cards" aria-label="Kartenansicht">
                            <CardIcon sx={{mr: {xs: 0, sm: 1}}}/>
                            <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>
                                Karten
                            </Box>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={() => handleOpenDialog()}
                        sx={{
                            minWidth: {xs: 'auto', sm: 'auto'},
                            px: {xs: 2, sm: 2}
                        }}
                    >
                        <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>
                            Neuer Übungsdienst
                        </Box>
                        <Box component="span" sx={{display: {xs: 'inline', sm: 'none'}}}>
                            Neu
                        </Box>
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 2}} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{mb: 2}} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {viewMode === 'table' ? (
                // Tabellenansicht
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Titel</strong></TableCell>
                                <TableCell><strong>Datum</strong></TableCell>
                                <TableCell><strong>Ort</strong></TableCell>
                                <TableCell><strong>Einheit</strong></TableCell>
                                <TableCell><strong>Teilnehmer</strong></TableCell>
                                <TableCell align="right"><strong>Aktionen</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="textSecondary" sx={{py: 4}}>
                                            Noch keine Übungsdienste vorhanden. Erstellen Sie den ersten!
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                Array.isArray(events) && events.map((event) => (
                                    <TableRow
                                        key={event.id}
                                        hover
                                        onClick={() => handleViewEvent(event.id)}
                                        sx={{cursor: 'pointer'}}
                                    >
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="600">
                                                {event.title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDateTime(event.eventDate)}
                                            </Typography>
                                            {event.eventEndDate && (
                                                <Typography variant="caption" color="textSecondary">
                                                    bis {formatDateTime(event.eventEndDate)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {event.location || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {event.unit?.name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">
                                                    <strong>{event.totalParticipants || 0}</strong> Gesamt
                                                </Typography>
                                                <Typography variant="caption" color="success.main">
                                                    {event.presentCount || 0} Anwesend
                                                </Typography>
                                                {' | '}
                                                <Typography variant="caption" color="error.main">
                                                    {event.absentCount || 0} Abwesend
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenDialog(event);
                                                }}
                                                size="small"
                                                title="Bearbeiten"
                                            >
                                                <EditIcon/>
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenDeleteDialog(event);
                                                }}
                                                size="small"
                                                title="Löschen"
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                // Kartenansicht
                <Box>
                    {events.length === 0 ? (
                        <Paper sx={{p: 4, textAlign: 'center'}}>
                            <Typography variant="body2" color="textSecondary">
                                Noch keine Übungsdienste vorhanden. Erstellen Sie den ersten!
                            </Typography>
                        </Paper>
                    ) : (
                        <>
                            {/* Kommende Termine */}
                            {getUpcomingEvents().length > 0 && (
                                <Box sx={{mb: 4}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
                                        <EventIcon color="primary"/>
                                        <Typography variant="h5" color="primary">
                                            Kommende Termine
                                        </Typography>
                                        <Chip
                                            label={getUpcomingEvents().length}
                                            color="primary"
                                            size="small"
                                        />
                                    </Box>
                                    <Grid container spacing={3}>
                                        {getUpcomingEvents().map((event) => (
                                            <Grid item xs={12} sm={6} md={4} key={event.id}>
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: 4
                                                        }
                                                    }}
                                                    onClick={() => handleViewEvent(event.id)}
                                                >
                                                    <CardContent sx={{flexGrow: 1}}>
                                                        <Typography variant="h6" component="div" gutterBottom>
                                                            {event.title}
                                                        </Typography>

                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                            <CalendarIcon fontSize="small" color="action"/>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatDate(event.eventDate)}, {formatTime(event.eventDate)}
                                                                {event.eventEndDate && <> - {formatTime(event.eventEndDate)}</>}
                                                            </Typography>
                                                        </Box>

                                                        {event.location && (
                                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                                <LocationIcon fontSize="small" color="action"/>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {event.location}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                                                            <GroupIcon fontSize="small" color="action"/>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {event.unit?.name || '-'}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2}}>
                                                            <Chip
                                                                label={`${event.totalParticipants || 0} Gesamt`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                label={`${event.presentCount || 0} Anwesend`}
                                                                size="small"
                                                                color="success"
                                                            />
                                                            <Chip
                                                                label={`${event.excusedCount || 0} Entsch.`}
                                                                size="small"
                                                                color="warning"
                                                            />
                                                            <Chip
                                                                label={`${event.absentCount || 0} Abwesend`}
                                                                size="small"
                                                                color="error"
                                                            />
                                                        </Box>
                                                    </CardContent>

                                                    <CardActions sx={{justifyContent: 'flex-end', p: 2, pt: 0}}>
                                                        <IconButton
                                                            color="primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDialog(event);
                                                            }}
                                                            size="small"
                                                            title="Bearbeiten"
                                                        >
                                                            <EditIcon/>
                                                        </IconButton>
                                                        <IconButton
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDeleteDialog(event);
                                                            }}
                                                            size="small"
                                                            title="Löschen"
                                                        >
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {/* Vergangene Termine */}
                            {getPastEvents().length > 0 && (
                                <Box>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
                                        <EventIcon color="action"/>
                                        <Typography variant="h5" color="text.secondary">
                                            Vergangene Termine
                                        </Typography>
                                        <Chip
                                            label={getPastEvents().length}
                                            color="default"
                                            size="small"
                                        />
                                    </Box>
                                    <Grid container spacing={3}>
                                        {getPastEvents().map((event) => (
                                            <Grid item xs={12} sm={6} md={4} key={event.id}>
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        opacity: 0.85,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: 4,
                                                            opacity: 1
                                                        }
                                                    }}
                                                    onClick={() => handleViewEvent(event.id)}
                                                >
                                                    <CardContent sx={{flexGrow: 1}}>
                                                        <Typography variant="h6" component="div" gutterBottom>
                                                            {event.title}
                                                        </Typography>

                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                            <CalendarIcon fontSize="small" color="action"/>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatDate(event.eventDate)}, {formatTime(event.eventDate)}
                                                                {event.eventEndDate && <> - {formatTime(event.eventEndDate)}</>}
                                                            </Typography>
                                                        </Box>

                                                        {event.location && (
                                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                                <LocationIcon fontSize="small" color="action"/>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {event.location}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                                                            <GroupIcon fontSize="small" color="action"/>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {event.unit?.name || '-'}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2}}>
                                                            <Chip
                                                                label={`${event.totalParticipants || 0} Gesamt`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                label={`${event.presentCount || 0} Anwesend`}
                                                                size="small"
                                                                color="success"
                                                            />
                                                            <Chip
                                                                label={`${event.excusedCount || 0} Entsch.`}
                                                                size="small"
                                                                color="warning"
                                                            />
                                                            <Chip
                                                                label={`${event.absentCount || 0} Abwesend`}
                                                                size="small"
                                                                color="error"
                                                            />
                                                        </Box>
                                                    </CardContent>

                                                    <CardActions sx={{justifyContent: 'flex-end', p: 2, pt: 0}}>
                                                        <IconButton
                                                            color="primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDialog(event);
                                                            }}
                                                            size="small"
                                                            title="Bearbeiten"
                                                        >
                                                            <EditIcon/>
                                                        </IconButton>
                                                        <IconButton
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDeleteDialog(event);
                                                            }}
                                                            size="small"
                                                            title="Löschen"
                                                        >
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>
                        {editingEvent ? 'Übungsdienst bearbeiten' : 'Neuer Übungsdienst'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 2}}>
                            <TextField
                                label="Titel"
                                value={formData.title}
                                onChange={(e) => setFormData({...formData, title: e.target.value})}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Beschreibung (optional)"
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                multiline
                                rows={3}
                                fullWidth
                            />
                            <TextField
                                label="Start"
                                type="datetime-local"
                                value={formData.eventDate}
                                onChange={(e) => setFormData({...formData, eventDate: e.target.value})}
                                required
                                fullWidth
                                slotProps={{inputLabel: {shrink: true}}}
                            />

                            {/* Toggle zwischen Dauer und manuellem Enddatum */}
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Typography variant="body2" color="textSecondary">
                                    Dauer wählen
                                </Typography>
                                <Switch
                                    checked={!useDuration}
                                    onChange={(e) => setUseDuration(!e.target.checked)}
                                    color="primary"
                                />
                                <Typography variant="body2" color="textSecondary">
                                    Enddatum eingeben
                                </Typography>
                            </Box>

                            {useDuration ? (
                                <TextField
                                    select
                                    label="Dauer"
                                    value={formData.duration}
                                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                                    required
                                    fullWidth
                                >
                                    <MenuItem value="30">30 Minuten</MenuItem>
                                    <MenuItem value="60">1 Stunde</MenuItem>
                                    <MenuItem value="90">1,5 Stunden</MenuItem>
                                    <MenuItem value="120">2 Stunden</MenuItem>
                                    <MenuItem value="150">2,5 Stunden</MenuItem>
                                    <MenuItem value="180">3 Stunden</MenuItem>
                                    <MenuItem value="210">3,5 Stunden</MenuItem>
                                    <MenuItem value="240">4 Stunden</MenuItem>
                                </TextField>
                            ) : (
                                <TextField
                                    label="Ende"
                                    type="datetime-local"
                                    value={formData.eventEndDate}
                                    onChange={(e) => setFormData({...formData, eventEndDate: e.target.value})}
                                    required
                                    fullWidth
                                    slotProps={{inputLabel: {shrink: true}}}
                                />
                            )}

                            <TextField
                                label="Ort"
                                value={formData.location}
                                onChange={(e) => setFormData({...formData, location: e.target.value})}
                                fullWidth
                            />
                            <TextField
                                select
                                label="Typ"
                                value={formData.type}
                                onChange={(e) => setFormData({...formData, type: e.target.value})}
                                required
                                fullWidth
                            >
                                <MenuItem value="UEBUNGSDIENST">Übungsdienst</MenuItem>
                                <MenuItem value="WETTKAMPF">Wettkampf</MenuItem>
                                <MenuItem value="VERANSTALTUNG">Veranstaltung</MenuItem>
                                <MenuItem value="SONSTIGES">Sonstiges</MenuItem>
                            </TextField>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Abbrechen</Button>
                        <Button type="submit" variant="contained">
                            {editingEvent ? 'Aktualisieren' : 'Erstellen'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Übungsdienst löschen?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Möchten Sie den Übungsdienst "{deletingEvent?.title}" wirklich löschen?
                        Diese Aktion kann nicht rückgängig gemacht werden.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Abbrechen</Button>
                    <Button onClick={handleDelete} variant="contained" color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ManageEvents;

